import React, { useState, useEffect } from 'react';
import FiltroUsuarios from './FiltroUsuarios';
import { useFiltroUsuarios } from '../filtroUsuarios';
import { calcularRFC, calcularCURP } from '../utils/calculoRfcCurp';
import { formatearMoneda, limpiarMoneda, valorCrudo } from '../utils/formatMoney';
import useResponsive from '../utils/useResponsive';

const gridCols = (isMobile, isTablet, desktopCols) => (isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : desktopCols);

const API = '/api';

const DESTINOS_AVALUO = [
  'Comprar tu Vivienda',
  'Construir tu Vivienda',
  'Ampliar, remodelar o Mejorar tu Vivienda',
  'Pagar la hipoteca de tu Vivienda',
  'Adquisición de suelo (Crediterreno Infonavit)',
];

const DESTINOS_CREDITO = [
  'Comprar una Vivienda',
  'Comprar Terreno',
  'Comprar Terreno y Construir una Vivienda',
  'Construir Vivienda',
  'Reparar, Ampliar o Mejorar la Vivienda',
  'Comprar y Mejorar la Vivienda',
  'Pagar el Pasivo o la Hipoteca de la Vivienda con garantía hipotecaria',
  'Pagar el Pasivo a la Hipoteca de la Vivienda con garantía del Saldo de la Subcuenta de Vivienda',
];

// Destinos que capturan Plazo de Derechohabiente/Coparticipante (máx. 30 años)
const DESTINOS_PLAZO_DH_COPART = ['Comprar una Vivienda', 'Construir Vivienda'];
// Las 2 variantes de "Pagar el Pasivo..." comparten el mismo grupo de Plazo y Monto de Deuda
const DESTINOS_PAGAR_PASIVO = [
  'Pagar el Pasivo o la Hipoteca de la Vivienda con garantía hipotecaria',
  'Pagar el Pasivo a la Hipoteca de la Vivienda con garantía del Saldo de la Subcuenta de Vivienda',
];
// Destinos que capturan Precio de Compra Venta (mutuamente excluyente con los otros montos)
const DESTINOS_PRECIO_COMPRA = ['Comprar una Vivienda', 'Comprar Terreno', 'Comprar Terreno y Construir una Vivienda', 'Comprar y Mejorar la Vivienda'];

const PRODUCTOS    = ['Infonavit', 'Infonavit Total', 'Cofinavit', 'Cofinavit Ingresos Adicionales'];
const BANCOS       = ['Afirme', 'Banorte', 'Banamex', 'Banregio', 'HSBC', 'Santander', 'Scotiabank'];
const TIPOS_CRED   = ['Individual', 'Conyugal', 'Corresidencial', 'Familiar'];
const SUB_CORRESI  = ['Pareja', 'Amigo'];
const SUB_FAMILIAR = ['Progenitor', 'Hijo (a)', 'Hermano (a)'];
const GENEROS      = ['Masculino', 'Femenino'];
const EST_CIVILES  = ['Soltero', 'Casado(a)'];
const REGIMENES    = ['Separación de Bienes', 'Sociedad Conyugal', 'Sociedad Legal'];
const INCAPACID    = ['Motriz', 'Auditiva', 'Mental', 'Visual'];
const DISC_QUIEN   = ['Derechohabiente Solicitante', 'Conyuge', 'Padre', 'Madre', 'Hijo'];

const stBase = {
  card: { backgroundColor: '#F0F0F0', padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 20 },
  title: { fontSize: 16, fontWeight: 'bold', color: '#1a3a6e', marginBottom: 16, borderBottom: '2px solid #e11d48', paddingBottom: 8 },
  sectionTitle: { fontSize: 14, fontWeight: '700', color: '#374151', marginBottom: 12, marginTop: 8 },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 },
  input: { width: '100%', height: '38px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
  select: { width: '100%', height: '38px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', backgroundColor: 'white' },
  btn: { padding: '9px 20px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: '600', fontSize: 13 },
  btnPrimary: { backgroundColor: '#1a3a6e', color: 'white' },
  btnSuccess: { backgroundColor: '#22c55e', color: 'white' },
  btnDanger: { backgroundColor: '#dc2626', color: 'white' },
  btnCancel: { backgroundColor: '#d1d5db', color: '#374151' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { backgroundColor: '#1a3a6e', color: 'white', padding: '10px 12px', textAlign: 'left', fontSize: 13 },
  td: { padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontSize: 13 },
  inputReadonly: { width: '100%', height: '38px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', backgroundColor: '#f3f4f6' },
  inputConBoton: { display: 'flex', gap: 6 },
  btnCalc: { padding: '8px 10px', border: '1px solid #1a3a6e', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: '600', backgroundColor: 'white', color: '#1a3a6e', whiteSpace: 'nowrap' },
  moneyWrap: { position: 'relative' },
  moneySign: { position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 },
};

const Txt = ({ label, value, onChange, placeholder = '', maxLength, type = 'text', disabled = false }) => (
  <div style={stBase.field}>
    {label && <label style={stBase.label}>{label}</label>}
    <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} maxLength={maxLength} disabled={disabled}
      style={disabled ? { ...stBase.input, background: '#f3f4f6', color: '#4b5563', cursor: 'not-allowed' } : stBase.input} />
  </div>
);

// Plazo en años, numérico de máximo 2 dígitos
const TxtPlazo = ({ label, value, onChange, maxAnios }) => (
  <div style={stBase.field}>
    <label style={stBase.label}>{label}{maxAnios ? ` (máx. ${maxAnios} años)` : ''}</label>
    <input type="text" inputMode="numeric" maxLength={2} value={value || ''}
      onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 2))}
      style={stBase.input} placeholder="Años" />
  </div>
);

// Campo monetario sin decimales (patrón formatearMoneda/limpiarMoneda/valorCrudo)
const Money = ({ label, value, enfocado, setEnfocado, onChange }) => (
  <div style={stBase.field}>
    <label style={stBase.label}>{label}</label>
    <div style={stBase.moneyWrap}>
      <span style={stBase.moneySign}>$</span>
      <input type="text" inputMode="decimal"
        value={enfocado ? valorCrudo(value, 0) : formatearMoneda(value, 0)}
        onFocus={() => setEnfocado(true)}
        onBlur={() => setEnfocado(false)}
        onChange={e => onChange(limpiarMoneda(e.target.value, 0))}
        style={{ ...stBase.input, paddingLeft: 24 }} placeholder="0" />
    </div>
  </div>
);

const Sel = ({ label, value, onChange, options }) => (
  <div style={stBase.field}>
    {label && <label style={stBase.label}>{label}</label>}
    <select value={value || ''} onChange={e => onChange(e.target.value)} style={stBase.select}>
      <option value="">— Selecciona —</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const EMPTY = {
  destino: '', broker_id: null,
  dh_nss: '', dh_ap: '', dh_am: '', dh_nombres: '',
  dh_calle_numero: '', dh_colonia: '', dh_municipio: '',
  dh_entidad_federativa: '', dh_cp: '', dh_lada: '', dh_telefono: '',
  prop_ap: '', prop_am: '', prop_nombre_razon_social: '',
  prop_rfc: '', prop_acreedor_hipotecario: '', prop_rfc_acreedor: '',
  prop_calle_numero: '', prop_colonia: '', prop_municipio: '',
  prop_estado: '', prop_cp: '', prop_lada: '', prop_telefono: '', prop_ext: '',
  viv_calle_numero: '', viv_numero_ext: '', viv_numero_int: '', viv_manzana: '', viv_lote: '',
  viv_colonia: '', viv_municipio: '', viv_estado: '', viv_cp: '', viv_antiguedad_anos: '',
  // Solicitud de Crédito
  producto: '', entidad_financiera: '', tipo_credito: '', tipo_credito_sub: '',
  destino_credito: '',
  plazo_dh: '', plazo_coparticipante: '', plazo_terreno: '', plazo_terreno_construccion: '',
  plazo_pasivo_dh: '', plazo_pasivo_coparticipante: '',
  discapacitado: '', tipo_incapacidad: '', discapacidad_quien: '',
  precio_compra_venta: '', monto_presupuesto_construccion: '', monto_presupuesto_reparacion: '', monto_deuda_pasivo: '',
  empresa_nombre: '', empresa_registro_patronal: '',
  empresa_lada: '', empresa_telefono: '', empresa_extension: '',
  // Identificación Conyugal / Familiar / Corresponsable
  conyugal_apellido_paterno: '', conyugal_apellido_materno: '', conyugal_nombres: '',
  conyugal_fecha_nacimiento: '', conyugal_genero: '', conyugal_entidad_nacimiento: '',
  conyugal_nss: '', conyugal_curp: '', conyugal_rfc: '',
  conyugal_celular: '', conyugal_correo_electronico: '', conyugal_lada: '', conyugal_telefono: '',
  conyugal_nombre_empresa: '', conyugal_numero_registro_patronal: '',
  conyugal_empresa_lada: '', conyugal_empresa_telefono: '', conyugal_empresa_extension: '',
  // DH adicional
  dh_curp: '', dh_rfc: '', dh_celular: '', dh_genero: '',
  dh_correo: '', dh_estado_civil: '', dh_regimen_matrimonial: '',
  dh_fecha_nacimiento: '', dh_entidad_nacimiento: '',
  // Co-participante
  co_nss: '', co_curp: '', co_rfc: '', co_ap: '', co_am: '', co_nombres: '',
  co_lada: '', co_telefono: '', co_celular: '', co_genero: '', co_correo: '',
  co_empresa_nombre: '', co_empresa_registro_patronal: '',
  co_empresa_lada: '', co_empresa_telefono: '', co_empresa_extension: '',
  // Referencias
  ref1_ap: '', ref1_am: '', ref1_nombres: '', ref1_lada: '', ref1_telefono: '', ref1_celular: '',
  ref2_ap: '', ref2_am: '', ref2_nombres: '', ref2_lada: '', ref2_telefono: '', ref2_celular: '',
  // Hoja 3 — Abono en cuenta y Acreedor Hipotecario
  abono_marcar: false,
  abono_nombre_l1: '', abono_nombre_l2: '', abono_rfc: '',
  abono_nombre_ec_l1: '', abono_nombre_ec_l2: '', abono_clabe: '',
  acreedor_nombre_l1: '', acreedor_nombre_l2: '', acreedor_rfc: '',
  acreedor_nombre_ec_l1: '', acreedor_nombre_ec_l2: '', acreedor_clabe: '',
  num_credito_titular: '', num_credito_coparticipante: '',
  oferta_vinculante: false,
};

export default function Infonavit({ usuarioActual }) {
  const { isMobile, isTablet } = useResponsive();
  const st = {
    ...stBase,
    grid2: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 14, marginBottom: 4 },
    grid3: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: 14, marginBottom: 4 },
    grid4: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 14, marginBottom: 4 },
  };
  const [view, setView]           = useState('lista');
  const [lista, setLista]         = useState([]);
  const [form, setForm]           = useState(EMPTY);
  const [editId, setEditId]       = useState(null);
  // El nombre del cliente ya grabado no se puede modificar (el backend también lo ignora).
  const [nombreBloqueado, setNombreBloqueado] = useState(false);
  const [estados, setEstados]     = useState([]);
  const [brokers, setBrokers]     = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [seleccionado, setSeleccionado] = useState(null);
  const [formatos, setFormatos]   = useState([]);
  const [coloniasDh, setColoniasDh]     = useState([]);
  const [coloniasProp, setColoniasProp] = useState([]);
  const [precioEnfocado, setPrecioEnfocado] = useState(false);
  const [montoConstruccionEnfocado, setMontoConstruccionEnfocado] = useState(false);
  const [montoReparacionEnfocado, setMontoReparacionEnfocado] = useState(false);
  const [montoDeudaEnfocado, setMontoDeudaEnfocado] = useState(false);
  const [exportarProspecto, setExportarProspecto] = useState('No');
  const [prospectosLista, setProspectosLista] = useState([]);
  const [prospectoSeleccionado, setProspectoSeleccionado] = useState('');
  const [filtroNombre, setFiltroNombre] = useState('');
  const [generandoPdf, setGenerandoPdf] = useState(null);
  const [misLimites, setMisLimites] = useState(null);
  const esAdmin = usuarioActual?.rol === 'administrador';
  const esAdminPlus = usuarioActual?.rol === 'administrador_plus';
  // Básico (rol semiusuario): ve Infonavit pero sin capturas salvo las que se le asignen
  // (limite_folios_infonavit_mes, 0 por default). El backend también lo frena.
  const esBasico = usuarioActual?.rol === 'semiusuario';
  const sinCapturas = esBasico && (!misLimites || (misLimites.limite_folios_infonavit_mes != null
    && misLimites.folios_infonavit_mes >= misLimites.limite_folios_infonavit_mes));
  const textoSinCapturas = misLimites && misLimites.limite_folios_infonavit_mes > 0
    ? 'Llegaste al límite de tus capturas del periodo. Contrata capturas extra para continuar.'
    : 'Tu plan no incluye capturas. Contrata capturas extra para continuar.';
  // Administrador Plus: lo de sus usuarios asignados es solo lectura (sin editar/eliminar/imprimir).
  const esAjeno = (row) => esAdminPlus && row && row.usuario_id !== usuarioActual.id;
  const filtroUsuariosKey = useFiltroUsuarios().join(',');

  // Se recarga también al cambiar el filtro de usuarios del Administrador Plus.
  useEffect(() => {
    cargarLista();
  }, [filtroUsuariosKey]);

  useEffect(() => {
    fetch(`${API}/sepomex/estados`).then(r => r.json()).then(d => setEstados(d)).catch(() => {});
    if (usuarioActual?.rol === 'administrador') {
      fetch(`${API}/brokers`).then(r => r.json()).then(d => setBrokers(Array.isArray(d) ? d : [])).catch(() => {});
    }
    fetch(`${API}/infonavit/formatos`).then(r => r.json()).then(d => setFormatos(Array.isArray(d) ? d : [])).catch(() => {});
    // Importar datos de un prospecto: solo de los propios (admin: todos).
    fetch(`${API}/prospectos`).then(r => r.json()).then(d => setProspectosLista(Array.isArray(d) ? d.filter(p => esAdmin || p.usuario_id === usuarioActual?.id) : [])).catch(() => {});
    if (!esAdmin) cargarMisLimites();
  }, []);

  const cargarLista = () => {
    fetch(`${API}/infonavit`).then(r => r.json()).then(d => setLista(Array.isArray(d) ? d : [])).catch(() => setLista([]));
  };

  const cargarMisLimites = () => {
    fetch(`${API}/mi-perfil/limites`).then(r => r.json()).then(d => setMisLimites(d)).catch(() => {});
  };

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const CP_CONFIG = {
    dh:   { colonia: 'dh_colonia',   municipio: 'dh_municipio',   estado: 'dh_entidad_federativa', setColonias: setColoniasDh },
    prop: { colonia: 'prop_colonia', municipio: 'prop_municipio', estado: 'prop_estado',            setColonias: setColoniasProp },
    viv:  { colonia: 'viv_colonia',  municipio: 'viv_municipio',  estado: 'viv_estado' },
  };

  const buscarColonias = async (prefijo, cp) => {
    const cfg = CP_CONFIG[prefijo];
    if (cp.length !== 5) { cfg.setColonias?.([]); return; }
    try {
      const r = await fetch(`${API}/sepomex/cp/${cp}`);
      const d = await r.json();
      if (d && d.length > 0) {
        cfg.setColonias?.(d);
        setForm(f => ({ ...f, [cfg.municipio]: d[0].municipio || d[0].ciudad || '', [cfg.estado]: d[0].estado || '' }));
      } else {
        cfg.setColonias?.([]);
        alert('Código postal no encontrado en SEPOMEX');
      }
    } catch (err) {
      console.error('Error buscando código postal:', err);
    }
  };

  const setCp = (prefijo, campoCp, valor) => {
    const cp = valor.replace(/\D/g, '');
    set(campoCp, cp);
    buscarColonias(prefijo, cp);
  };

  const calcularDhRfcCurp = (campo) => {
    if (!form.dh_nombres || !form.dh_ap || !form.dh_fecha_nacimiento) {
      alert('Para calcular, primero captura nombre(s), apellido paterno y fecha de nacimiento.');
      return;
    }
    const persona = {
      nombres: form.dh_nombres, apellido_paterno: form.dh_ap, apellido_materno: form.dh_am,
      fecha_nacimiento: form.dh_fecha_nacimiento,
    };
    if (campo === 'rfc') {
      set('dh_rfc', calcularRFC(persona));
    } else {
      if (!form.dh_genero || !form.dh_entidad_nacimiento) {
        alert('Para calcular el CURP, también captura género y entidad de nacimiento.');
        return;
      }
      set('dh_curp', calcularCURP({ ...persona, sexo_genero_id: form.dh_genero === 'Masculino' ? '1' : '2', entidad_nacimiento: form.dh_entidad_nacimiento }));
    }
  };

  const calcularConyugalRfcCurp = (campo) => {
    if (!form.conyugal_nombres || !form.conyugal_apellido_paterno || !form.conyugal_fecha_nacimiento) {
      alert('Para calcular, primero captura nombre(s), apellido paterno y fecha de nacimiento.');
      return;
    }
    const persona = {
      nombres: form.conyugal_nombres, apellido_paterno: form.conyugal_apellido_paterno, apellido_materno: form.conyugal_apellido_materno,
      fecha_nacimiento: form.conyugal_fecha_nacimiento,
    };
    if (campo === 'rfc') {
      set('conyugal_rfc', calcularRFC(persona));
    } else {
      if (!form.conyugal_genero || !form.conyugal_entidad_nacimiento) {
        alert('Para calcular el CURP, también captura género y entidad de nacimiento.');
        return;
      }
      set('conyugal_curp', calcularCURP({ ...persona, sexo_genero_id: form.conyugal_genero === 'Masculino' ? '1' : '2', entidad_nacimiento: form.conyugal_entidad_nacimiento }));
    }
  };

  const importarProspecto = async (prospectoId) => {
    if (!prospectoId) return;
    try {
      const r = await fetch(`${API}/prospectos/${prospectoId}`);
      const d = await r.json();
      const t = d.titular;
      if (!t) { alert('Este prospecto no tiene datos de Titular capturados.'); return; }
      const c = d.coacreditado;
      const calleNumero = [t.calle, t.no_ext].filter(Boolean).join(' ') + (t.no_int ? ` Int. ${t.no_int}` : '');
      const tel = (t.telefono_casa || '').replace(/\D/g, '');
      const telEmpresa = (t.trabajo_telefono || '').replace(/\D/g, '');
      const telC = (c?.telefono_casa || '').replace(/\D/g, '');
      const telEmpresaC = (c?.trabajo_telefono || '').replace(/\D/g, '');
      setForm(f => {
        const nuevo = {
          ...f,
          ...(nombreBloqueado ? {} : {
            dh_ap: t.apellido_paterno || '',
            dh_am: t.apellido_materno || '',
            dh_nombres: t.nombres || '',
          }),
          dh_fecha_nacimiento: t.fecha_nacimiento ? String(t.fecha_nacimiento).split('T')[0] : '',
          dh_genero: t.sexo_genero_id === 1 ? 'Masculino' : (t.sexo_genero_id === 2 ? 'Femenino' : ''),
          dh_entidad_nacimiento: t.entidad_nacimiento || '',
          dh_nss: t.numero_imss || '',
          dh_curp: t.curp || '',
          dh_rfc: t.rfc || '',
          dh_calle_numero: calleNumero.trim(),
          dh_cp: t.codigo_postal || '',
          dh_colonia: t.colonia || '',
          dh_municipio: t.ciudad || '',
          dh_entidad_federativa: t.entidad_federativa || '',
          dh_lada: tel.slice(0, 3),
          dh_telefono: tel.slice(3),
          dh_celular: t.telefono_celular || '',
          dh_correo: t.email || '',
          empresa_nombre: t.nombre_empresa || '',
          empresa_lada: telEmpresa.slice(0, 3),
          empresa_telefono: telEmpresa.slice(3),
        };
        if (f.tipo_credito === 'Conyugal') {
          if (c) {
            Object.assign(nuevo, {
              conyugal_apellido_paterno: c.apellido_paterno || '',
              conyugal_apellido_materno: c.apellido_materno || '',
              conyugal_nombres: c.nombres || '',
              conyugal_fecha_nacimiento: c.fecha_nacimiento ? String(c.fecha_nacimiento).split('T')[0] : '',
              conyugal_genero: c.sexo_genero_id === 1 ? 'Masculino' : (c.sexo_genero_id === 2 ? 'Femenino' : ''),
              conyugal_entidad_nacimiento: c.entidad_nacimiento || '',
              conyugal_nss: c.numero_imss || '',
              conyugal_curp: c.curp || '',
              conyugal_rfc: c.rfc || '',
              conyugal_lada: telC.slice(0, 3),
              conyugal_telefono: telC.slice(3),
              conyugal_celular: c.telefono_celular || '',
              conyugal_correo_electronico: c.email || '',
              conyugal_nombre_empresa: c.nombre_empresa || '',
              conyugal_empresa_lada: telEmpresaC.slice(0, 3),
              conyugal_empresa_telefono: telEmpresaC.slice(3),
              conyugal_empresa_extension: c.trabajo_extension || '',
            });
          } else {
            alert('El Tipo de Crédito es Conyugal, pero este prospecto no tiene datos de Coacreditado capturados.');
          }
        }
        return nuevo;
      });
      if (t.codigo_postal) buscarColonias('dh', t.codigo_postal);
    } catch (err) {
      console.error('Error importando prospecto:', err);
      alert('Error al importar datos del prospecto');
    }
  };

  const nuevo = () => {
    if (!esAdmin && misLimites?.limite_folios_infonavit_mes != null && misLimites.folios_infonavit_mes >= misLimites.limite_folios_infonavit_mes) {
      alert(`Ya llegó a su límite permitido de ${misLimites.limite_folios_infonavit_mes} folios de Infonavit para este mes.`);
      return;
    }
    const brokerInicial = !esAdmin ? (usuarioActual.broker_id || null) : EMPTY.broker_id;
    setForm({ ...EMPTY, broker_id: brokerInicial });
    setEditId(null);
    setNombreBloqueado(false);
    setExportarProspecto('No');
    setProspectoSeleccionado('');
    setView('form');
  };

  const editar = async (id) => {
    const r = await fetch(`${API}/infonavit/${id}`);
    const d = await r.json();
    setForm({ ...EMPTY, ...d });
    setEditId(id);
    setNombreBloqueado(!!(d.dh_ap || d.dh_am || d.dh_nombres));
    setExportarProspecto('No');
    setProspectoSeleccionado('');
    setView('form');
    if (d.dh_cp) buscarColonias('dh', d.dh_cp);
    if (d.prop_cp) buscarColonias('prop', d.prop_cp);
    if (d.viv_cp) buscarColonias('viv', d.viv_cp);
  };

  const guardar = async () => {
    setGuardando(true);
    const esNuevo = !editId;
    try {
      const url    = editId ? `${API}/infonavit/${editId}` : `${API}/infonavit`;
      const method = editId ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const d = await r.json();
      if (!d.success && !d.id) { alert('Error al guardar: ' + (d.error || 'Error desconocido')); return; }
      cargarLista();
      setView('lista');
      if (esNuevo && !esAdmin) cargarMisLimites();
    } catch (err) {
      alert('Error al guardar: ' + err.message);
    } finally {
      setGuardando(false);
    }
  };

  const NOMBRES_ARCHIVO_INFONAVIT = {
    'solicitud-avaluo':  'Solicitud Avaluo Infonavit',
    'solicitud-credito': 'Solicitud Credito Infonavit',
    'carta-instruccion-cofinavit-titular':  'Carta Instruccion Irrevocable Cofinavit Titular',
    'carta-instruccion-cofinavit-conyugal': 'Carta Instruccion Irrevocable Conyugal',
  };

  const PRODUCTOS_COFINAVIT = ['Cofinavit', 'Cofinavit Ingresos Adicionales'];

  const formatoDisponible = (fmt, prospecto) => {
    if (fmt.id === 'carta-instruccion-cofinavit-titular') {
      return PRODUCTOS_COFINAVIT.includes(prospecto?.producto);
    }
    if (fmt.id === 'carta-instruccion-cofinavit-conyugal') {
      return PRODUCTOS_COFINAVIT.includes(prospecto?.producto) && !!prospecto?.tipo_credito && prospecto.tipo_credito !== 'Individual';
    }
    return true;
  };

  const generarPDF = async (prospectoId, fmt) => {
    setGenerandoPdf(fmt.id);
    try {
      const url = `${API}/infonavit/pdf/${prospectoId}/${fmt.id}`;
      const response = await fetch(url);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error generando PDF');
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      const nombreArchivo = NOMBRES_ARCHIVO_INFONAVIT[fmt.id] || `${fmt.id}-Infonavit`;
      a.download = `${nombreArchivo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setGenerandoPdf(null);
    }
  };

  const nombreCompleto = (row) => {
    const p = [row.dh_ap, row.dh_am, row.dh_nombres].filter(Boolean);
    return p.length ? p.join(' ') : '(Sin nombre)';
  };

  // ── VISTA LISTA ──────────────────────────────────────────────────────────
  if (view === 'lista') {
    const listaFiltrada = lista.filter(row =>
      nombreCompleto(row).toLowerCase().includes(filtroNombre.trim().toLowerCase())
    );
    return (
      <div>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <h2 style={{ fontSize: 22, fontWeight: 'bold', color: '#1a3a6e', margin: 0 }}>Créditos Infonavit / Cofinavit</h2>
          <button onClick={sinCapturas ? undefined : nuevo} disabled={sinCapturas} title={sinCapturas ? textoSinCapturas : undefined}
            style={{ ...st.btn, ...st.btnSuccess, ...(sinCapturas ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>+ Nuevo Prospecto</button>
        </div>
        {sinCapturas && misLimites && (
          <div style={{ padding: '12px 16px', backgroundColor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, color: '#92400e', fontSize: 14, marginBottom: 16 }}>
            🔒 {textoSinCapturas}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-end', gap: 12, marginBottom: 14 }}>
          <div style={{ width: isMobile ? '100%' : '30%', minWidth: isMobile ? 'auto' : 220 }}>
            <label style={st.label}>Buscar por nombre de cliente</label>
            <input
              type="text"
              value={filtroNombre}
              onChange={e => setFiltroNombre(e.target.value)}
              placeholder="Escribe el nombre del cliente..."
              style={st.input}
            />
          </div>
          {esAdminPlus && <FiltroUsuarios ancho={isMobile ? '100%' : 300} />}
          {!esAdmin && misLimites && (
            <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1a3a6e' }}>
              Folios Creados en el mes: {misLimites.folios_infonavit_mes}{misLimites.limite_folios_infonavit_mes != null ? ` / ${misLimites.limite_folios_infonavit_mes}` : ''}
            </div>
          )}
        </div>

        {isMobile ? (
          <div style={{ marginBottom: 24 }}>
            {listaFiltrada.length === 0 && (
              <div style={{ backgroundColor: 'white', borderRadius: 8, padding: 32, textAlign: 'center', color: '#6b7280', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                No hay prospectos registrados
              </div>
            )}
            {listaFiltrada.map(row => {
              const esSel = seleccionado?.id === row.id;
              return (
                <div key={row.id} onClick={() => setSeleccionado(esSel ? null : row)}
                  style={{ backgroundColor: esSel ? '#dbeafe' : 'white', borderRadius: 8, padding: 14, marginBottom: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', cursor: 'pointer', borderLeft: esSel ? '4px solid #1a3a6e' : '4px solid transparent' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <span style={{ fontWeight: '600', fontSize: 14 }}>{nombreCompleto(row)}</span>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>#{row.id}{esAdminPlus ? ` · ${row.usuario_nombre || '-'}` : ''}</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#374151', marginBottom: 4 }}>{row.destino_credito || row.destino || '—'}</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                    {row.created_at ? new Date(row.created_at).toLocaleDateString('es-MX') : '—'}
                  </div>
                  <div style={{ display: 'flex', gap: 16, borderTop: '1px solid #f0f0f0', paddingTop: 10 }} onClick={e => e.stopPropagation()}>
                    {esAjeno(row) ? (
                      <button onClick={() => editar(row.id)} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: 13, padding: 0 }}>👁 Consultar</button>
                    ) : (
                      <button onClick={() => editar(row.id)} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: 13, padding: 0 }}>Editar</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 24, overflowX: 'auto' }}>
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={st.th}>ID</th>
                  <th style={st.th}>Derechohabiente</th>
                  {esAdminPlus && <th style={st.th}>Usuario</th>}
                  <th style={st.th}>Destino de Crédito</th>
                  <th style={st.th}>Fecha</th>
                  <th style={{ ...st.th, textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {listaFiltrada.length === 0 && (
                  <tr><td colSpan={esAdminPlus ? 6 : 5} style={{ ...st.td, textAlign: 'center', color: '#6b7280', padding: 32 }}>No hay prospectos registrados</td></tr>
                )}
                {listaFiltrada.map((row, i) => {
                  const esSel = seleccionado?.id === row.id;
                  return (
                    <tr key={row.id} onClick={() => setSeleccionado(esSel ? null : row)}
                      style={{ backgroundColor: esSel ? '#dbeafe' : (i % 2 ? 'white' : '#f9fafb'), cursor: 'pointer', borderLeft: esSel ? '4px solid #1a3a6e' : '4px solid transparent' }}>
                      <td style={st.td}>{row.id}</td>
                      <td style={{ ...st.td, fontWeight: '600' }}>{nombreCompleto(row)}</td>
                      {esAdminPlus && <td style={st.td}>{row.usuario_nombre || '-'}</td>}
                      <td style={st.td}>{row.destino_credito || row.destino || '—'}</td>
                      <td style={st.td}>{row.created_at ? new Date(row.created_at).toLocaleDateString('es-MX') : '—'}</td>
                      <td style={{ ...st.td, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                        {esAjeno(row) ? (
                          <button onClick={() => editar(row.id)} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}>👁 Consultar</button>
                        ) : (
                          <button onClick={() => editar(row.id)} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}>Editar</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {seleccionado && esAjeno(seleccionado) ? (
          <div style={{ padding: '16px 18px', backgroundColor: '#fef3c7', borderRadius: 8, border: '1px solid #fcd34d', color: '#92400e', fontSize: 13, textAlign: 'center' }}>
            👁 Este registro pertenece a otro usuario: puede consultarlo, pero solo su dueño puede generar los formatos.
          </div>
        ) : seleccionado ? (
          <div>
            <div style={{ padding: '12px 18px', backgroundColor: '#e7f3ff', borderRadius: 8, border: '1px solid #b3d7ff', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontWeight: 'bold', color: '#1a3a6e', fontSize: 15 }}>{nombreCompleto(seleccionado)}</span>
              {seleccionado.destino_credito && <span style={{ color: '#4b5563', fontSize: 13 }}>— {seleccionado.destino_credito}</span>}
            </div>
            <div style={{ border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', backgroundColor: '#1a3a6e', color: 'white' }}>
                <span style={{ fontWeight: 'bold', fontSize: 15 }}>Formatos — Infonavit</span>
              </div>
              <div style={{ display: 'grid', gap: 1, backgroundColor: '#f0f0f0' }}>
                {formatos.filter(fmt => formatoDisponible(fmt, seleccionado)).map(fmt => (
                  <div key={fmt.id} style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 10 : 0, padding: '12px 18px', backgroundColor: 'white' }}>
                    <div>
                      <strong style={{ fontSize: 14 }}>{fmt.nombre}</strong>
                      {fmt.descripcion && <p style={{ margin: '3px 0 0', color: '#666', fontSize: 12 }}>{fmt.descripcion}</p>}
                    </div>
                    <button onClick={() => generarPDF(seleccionado.id, fmt)} disabled={generandoPdf === fmt.id}
                      style={{ padding: '8px 18px', backgroundColor: '#f97316', color: 'white', border: 'none', borderRadius: 6, cursor: generandoPdf === fmt.id ? 'wait' : 'pointer', fontWeight: 'bold', minWidth: isMobile ? 'auto' : 130, width: isMobile ? '100%' : 'auto', fontSize: 13, opacity: generandoPdf === fmt.id ? 0.7 : 1 }}>
                      {generandoPdf === fmt.id ? '⏳ Generando…' : '📄 Generar PDF'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ padding: '16px 18px', backgroundColor: '#f9fafb', borderRadius: 8, border: '1px dashed #d1d5db', color: '#6b7280', fontSize: 13, textAlign: 'center' }}>
            Selecciona un derechohabiente de la lista para ver los formatos disponibles
          </div>
        )}
      </div>
    );
  }

  // ── HELPERS PARA EL FORMULARIO ───────────────────────────────────────────
  const SelEdo = ({ field, label = 'Entidad Federativa' }) => (
    <div style={st.field}>
      <label style={st.label}>{label}</label>
      <select value={form[field] || ''} onChange={e => set(field, e.target.value)} style={st.select}>
        <option value="">— Selecciona —</option>
        {estados.map(e => <option key={e.estado} value={e.estado}>{e.estado}</option>)}
      </select>
    </div>
  );

  const coParticipanteActivo = ['Conyugal', 'Corresidencial', 'Familiar'].includes(form.tipo_credito);

  // ── VISTA FORMULARIO ─────────────────────────────────────────────────────
  const soloLectura = !!editId && esAjeno(form);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => setView('lista')} style={{ fontSize: 20, background: 'none', border: 'none', cursor: 'pointer', color: '#4b5563' }}>←</button>
        <h2 style={{ fontSize: 20, fontWeight: 'bold', color: '#1a3a6e', margin: 0 }}>
          {soloLectura ? '👁 Consultar Prospecto Infonavit' : editId ? 'Editar Prospecto Infonavit' : 'Nuevo Prospecto Infonavit'}
        </h2>
      </div>
      {soloLectura && (
        <div style={{ backgroundColor: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 14 }}>
          👁 <strong>Solo lectura</strong> — este registro pertenece a otro usuario; puede consultarlo pero no modificarlo ni imprimirlo.
        </div>
      )}
      <fieldset disabled={soloLectura} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

      {/* ── DATOS DEL CRÉDITO (SOLICITUD DE CRÉDITO) ── */}
      <div style={st.card}>
        <div style={st.title}>Datos del Crédito</div>
        <div style={st.grid4}>
          <Sel label="Producto" value={form.producto} onChange={v => set('producto', v)} options={PRODUCTOS} />
          <Sel label="Entidad Financiera" value={form.entidad_financiera} onChange={v => set('entidad_financiera', v)} options={BANCOS} />
          <div>
            <Sel label="Tipo de Crédito" value={form.tipo_credito}
              onChange={v => { set('tipo_credito', v); set('tipo_credito_sub', ''); }} options={TIPOS_CRED} />
            {form.tipo_credito === 'Corresidencial' && (
              <Sel label="Sub-tipo Corresidencial" value={form.tipo_credito_sub} onChange={v => set('tipo_credito_sub', v)} options={SUB_CORRESI} />
            )}
            {form.tipo_credito === 'Familiar' && (
              <Sel label="Sub-tipo Familiar" value={form.tipo_credito_sub} onChange={v => set('tipo_credito_sub', v)} options={SUB_FAMILIAR} />
            )}
          </div>
          <Sel label="Destino del Crédito" value={form.destino_credito} onChange={v => set('destino_credito', v)} options={DESTINOS_CREDITO} />
        </div>
        {DESTINOS_PLAZO_DH_COPART.includes(form.destino_credito) && (
          <div style={st.grid3}>
            <TxtPlazo label="Plazo Derechohabiente" maxAnios={30} value={form.plazo_dh} onChange={v => set('plazo_dh', v)} />
            <TxtPlazo label="Plazo Coparticipante" maxAnios={30} value={form.plazo_coparticipante} onChange={v => set('plazo_coparticipante', v)} />
            <div />
          </div>
        )}
        {form.destino_credito === 'Comprar Terreno' && (
          <div style={st.grid3}>
            <TxtPlazo label="Plazo" maxAnios={15} value={form.plazo_terreno} onChange={v => set('plazo_terreno', v)} />
            <div /><div />
          </div>
        )}
        {form.destino_credito === 'Comprar Terreno y Construir una Vivienda' && (
          <div style={st.grid3}>
            <TxtPlazo label="Plazo" maxAnios={30} value={form.plazo_terreno_construccion} onChange={v => set('plazo_terreno_construccion', v)} />
            <div /><div />
          </div>
        )}
        {DESTINOS_PAGAR_PASIVO.includes(form.destino_credito) && (
          <div style={st.grid3}>
            <TxtPlazo label="Plazo Derechohabiente" maxAnios={30} value={form.plazo_pasivo_dh} onChange={v => set('plazo_pasivo_dh', v)} />
            <TxtPlazo label="Plazo Coparticipante" maxAnios={30} value={form.plazo_pasivo_coparticipante} onChange={v => set('plazo_pasivo_coparticipante', v)} />
            <div />
          </div>
        )}

        <div style={st.grid3}>
          {DESTINOS_PRECIO_COMPRA.includes(form.destino_credito) && (
            <Money label="Precio de Compra Venta" value={form.precio_compra_venta} enfocado={precioEnfocado} setEnfocado={setPrecioEnfocado} onChange={v => set('precio_compra_venta', v)} />
          )}
          {form.destino_credito === 'Construir Vivienda' && (
            <Money label="Monto de Presupuesto" value={form.monto_presupuesto_construccion} enfocado={montoConstruccionEnfocado} setEnfocado={setMontoConstruccionEnfocado} onChange={v => set('monto_presupuesto_construccion', v)} />
          )}
          {form.destino_credito === 'Reparar, Ampliar o Mejorar la Vivienda' && (
            <Money label="Monto de Presupuesto" value={form.monto_presupuesto_reparacion} enfocado={montoReparacionEnfocado} setEnfocado={setMontoReparacionEnfocado} onChange={v => set('monto_presupuesto_reparacion', v)} />
          )}
          {DESTINOS_PAGAR_PASIVO.includes(form.destino_credito) && (
            <Money label="Monto de la Deuda" value={form.monto_deuda_pasivo} enfocado={montoDeudaEnfocado} setEnfocado={setMontoDeudaEnfocado} onChange={v => set('monto_deuda_pasivo', v)} />
          )}
        </div>
      </div>

      {/* ── EXPORTAR INFO DE PROSPECTOS ── */}
      <div style={st.card}>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '1fr 2fr'), gap: 14 }}>
          <div style={{ maxWidth: isMobile ? '100%' : '50%' }}>
            <Sel label="Exportar Info de Prospectos" value={exportarProspecto}
              onChange={v => { setExportarProspecto(v); if (v !== 'Si') setProspectoSeleccionado(''); }} options={['Si', 'No']} />
          </div>
          {exportarProspecto === 'Si' && (
            <div style={st.field}>
              <label style={st.label}>Seleccionar Prospecto</label>
              <select value={prospectoSeleccionado}
                onChange={e => { setProspectoSeleccionado(e.target.value); importarProspecto(e.target.value); }} style={st.select}>
                <option value="">— Selecciona —</option>
                {prospectosLista.map(p => (
                  <option key={p.id} value={p.id}>{p.titular_nombre ? p.titular_nombre.trim() : `Prospecto #${p.id}`}{p.folio ? ` — ${p.folio}` : ''}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* ── DATOS DEL DERECHOHABIENTE ── */}
      <div style={st.card}>
        <div style={st.title}>Datos del Derechohabiente</div>
        <div style={st.grid3}>
          <Txt label="Apellido Paterno" value={form.dh_ap} onChange={v => set('dh_ap', v)} disabled={nombreBloqueado} />
          <Txt label="Apellido Materno" value={form.dh_am} onChange={v => set('dh_am', v)} disabled={nombreBloqueado} />
          <Txt label="Nombre(s)" value={form.dh_nombres} onChange={v => set('dh_nombres', v)} disabled={nombreBloqueado} />
        </div>
        {nombreBloqueado && !soloLectura && (
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: -6, marginBottom: 10 }}>🔒 El nombre del cliente no se puede modificar una vez grabado.</div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '13fr 10fr 16fr 13fr 19fr 15fr 14fr'), gap: 14, marginBottom: 4 }}>
          <div style={st.field}>
            <label style={st.label}>Fecha de Nacimiento</label>
            <input type="date" value={form.dh_fecha_nacimiento} onChange={e => set('dh_fecha_nacimiento', e.target.value)} style={st.input} />
          </div>
          <Sel label="Género" value={form.dh_genero} onChange={v => set('dh_genero', v)} options={GENEROS} />
          <SelEdo field="dh_entidad_nacimiento" label="Entidad de Nacimiento" />
          <Txt label="No. Seguro Social (NSS)" value={form.dh_nss} onChange={v => set('dh_nss', v)} maxLength={11} />
          <div style={st.field}>
            <label style={st.label}>CURP</label>
            <div style={st.inputConBoton}>
              <input maxLength={18} value={form.dh_curp} onChange={e => set('dh_curp', e.target.value.toUpperCase())} style={st.input} />
              <button type="button" onClick={() => calcularDhRfcCurp('curp')} style={st.btnCalc}>Calcular</button>
            </div>
          </div>
          <div style={st.field}>
            <label style={st.label}>RFC</label>
            <div style={st.inputConBoton}>
              <input maxLength={13} value={form.dh_rfc} onChange={e => set('dh_rfc', e.target.value.toUpperCase())} style={st.input} />
              <button type="button" onClick={() => calcularDhRfcCurp('rfc')} style={st.btnCalc}>Calcular</button>
            </div>
          </div>
          <div />
        </div>

        <div style={st.sectionTitle}>Domicilio del Derechohabiente</div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '1.4fr 0.25fr 1.35fr'), gap: 14, marginBottom: 4 }}>
          <Txt label="Calle y Número" value={form.dh_calle_numero} onChange={v => set('dh_calle_numero', v)} />
          <div style={st.field}>
            <label style={st.label}>C.P.</label>
            <input value={form.dh_cp} maxLength={5} onChange={e => setCp('dh', 'dh_cp', e.target.value)} style={st.input} placeholder="5 dígitos" />
          </div>
          <div />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '2.4fr 2.25fr 2fr 0.5fr 0.75fr 2.6fr'), gap: 14, marginBottom: 4 }}>
          <div style={st.field}>
            <label style={st.label}>Colonia</label>
            <select value={form.dh_colonia} onChange={e => set('dh_colonia', e.target.value)} style={st.select}>
              <option value="">— Selecciona —</option>
              {coloniasDh.map((c, i) => <option key={i} value={c.colonia}>{c.colonia}</option>)}
            </select>
          </div>
          <div style={st.field}>
            <label style={st.label}>Municipio o Delegación</label>
            <input value={form.dh_municipio} readOnly style={st.inputReadonly} />
          </div>
          <div style={st.field}>
            <label style={st.label}>Entidad Federativa</label>
            <input value={form.dh_entidad_federativa} readOnly style={st.inputReadonly} />
          </div>
          <Txt label="Lada" value={form.dh_lada} onChange={v => set('dh_lada', v)} maxLength={6} />
          <Txt label="Teléfono" value={form.dh_telefono} onChange={v => set('dh_telefono', v)} maxLength={10} />
          <div />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '10fr 30fr 12fr 48fr'), gap: 14, marginBottom: 4 }}>
          <Txt label="Celular" value={form.dh_celular} onChange={v => set('dh_celular', v)} maxLength={10} />
          <Txt label="Correo Electrónico" value={form.dh_correo} onChange={v => set('dh_correo', v)} />
          <Sel label="Estado Civil" value={form.dh_estado_civil} onChange={v => { set('dh_estado_civil', v); if (v !== 'Casado(a)') set('dh_regimen_matrimonial', ''); }} options={EST_CIVILES} />
          <div />
        </div>
        {form.dh_estado_civil === 'Casado(a)' && (
          <div style={{ width: isMobile ? '100%' : '50%' }}>
            <Sel label="Régimen Patrimonial del Matrimonio" value={form.dh_regimen_matrimonial} onChange={v => set('dh_regimen_matrimonial', v)} options={REGIMENES} />
          </div>
        )}
      </div>

      {/* ── EMPRESA / PATRÓN DEL DERECHOHABIENTE ── */}
      <div style={st.card}>
        <div style={st.title}>Empresa / Patrón del Derechohabiente</div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '40fr 15fr 45fr'), gap: 14, marginBottom: 4 }}>
          <Txt label="Nombre de la Empresa o Patrón" value={form.empresa_nombre} onChange={v => set('empresa_nombre', v)} />
          <Txt label="Número de Registro Patronal" value={form.empresa_registro_patronal} onChange={v => set('empresa_registro_patronal', v)} />
          <div />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '5fr 15fr 10fr 70fr'), gap: 14, marginBottom: 4 }}>
          <Txt label="Lada" value={form.empresa_lada} onChange={v => set('empresa_lada', v)} maxLength={6} />
          <Txt label="Teléfono de la Empresa" value={form.empresa_telefono} onChange={v => set('empresa_telefono', v)} maxLength={10} />
          <Txt label="Extensión" value={form.empresa_extension} onChange={v => set('empresa_extension', v)} maxLength={10} />
          <div />
        </div>
      </div>

      {/* ── DATOS DE IDENTIFICACIÓN EN CRÉDITO CONYUGAL, FAMILIAR O CORRESPONSABLE ── */}
      {coParticipanteActivo && (
      <div style={st.card}>
        <div style={st.title}>Datos Co-participante para credito Conyugal, Familiar o Corresidencial</div>
        <div style={st.grid3}>
          <Txt label="Apellido Paterno" value={form.conyugal_apellido_paterno} onChange={v => set('conyugal_apellido_paterno', v)} />
          <Txt label="Apellido Materno" value={form.conyugal_apellido_materno} onChange={v => set('conyugal_apellido_materno', v)} />
          <Txt label="Nombre(s)" value={form.conyugal_nombres} onChange={v => set('conyugal_nombres', v)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '13fr 10fr 16fr 13fr 19fr 15fr 14fr'), gap: 14, marginBottom: 4 }}>
          <div style={st.field}>
            <label style={st.label}>Fecha de Nacimiento</label>
            <input type="date" value={form.conyugal_fecha_nacimiento} onChange={e => set('conyugal_fecha_nacimiento', e.target.value)} style={st.input} />
          </div>
          <Sel label="Género" value={form.conyugal_genero} onChange={v => set('conyugal_genero', v)} options={GENEROS} />
          <SelEdo field="conyugal_entidad_nacimiento" label="Entidad de Nacimiento" />
          <Txt label="No. Seguro Social (NSS)" value={form.conyugal_nss} onChange={v => set('conyugal_nss', v)} maxLength={11} />
          <div style={st.field}>
            <label style={st.label}>CURP</label>
            <div style={st.inputConBoton}>
              <input maxLength={18} value={form.conyugal_curp} onChange={e => set('conyugal_curp', e.target.value.toUpperCase())} style={st.input} />
              <button type="button" onClick={() => calcularConyugalRfcCurp('curp')} style={st.btnCalc}>Calcular</button>
            </div>
          </div>
          <div style={st.field}>
            <label style={st.label}>RFC</label>
            <div style={st.inputConBoton}>
              <input maxLength={13} value={form.conyugal_rfc} onChange={e => set('conyugal_rfc', e.target.value.toUpperCase())} style={st.input} />
              <button type="button" onClick={() => calcularConyugalRfcCurp('rfc')} style={st.btnCalc}>Calcular</button>
            </div>
          </div>
          <div />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '10fr 30fr 8fr 12fr 40fr'), gap: 14, marginBottom: 4 }}>
          <Txt label="Celular" value={form.conyugal_celular} onChange={v => set('conyugal_celular', v)} maxLength={10} />
          <Txt label="Correo Electrónico" value={form.conyugal_correo_electronico} onChange={v => set('conyugal_correo_electronico', v)} />
          <Txt label="Lada" value={form.conyugal_lada} onChange={v => set('conyugal_lada', v)} maxLength={6} />
          <Txt label="Teléfono" value={form.conyugal_telefono} onChange={v => set('conyugal_telefono', v)} maxLength={10} />
          <div />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '40fr 15fr 45fr'), gap: 14, marginBottom: 4 }}>
          <Txt label="Nombre de la Empresa o Patrón" value={form.conyugal_nombre_empresa} onChange={v => set('conyugal_nombre_empresa', v)} />
          <Txt label="Número de Registro Patronal" value={form.conyugal_numero_registro_patronal} onChange={v => set('conyugal_numero_registro_patronal', v)} />
          <div />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '5fr 15fr 10fr 70fr'), gap: 14, marginBottom: 4 }}>
          <Txt label="Lada" value={form.conyugal_empresa_lada} onChange={v => set('conyugal_empresa_lada', v)} maxLength={6} />
          <Txt label="Teléfono de la Empresa" value={form.conyugal_empresa_telefono} onChange={v => set('conyugal_empresa_telefono', v)} maxLength={10} />
          <Txt label="Extensión" value={form.conyugal_empresa_extension} onChange={v => set('conyugal_empresa_extension', v)} maxLength={10} />
          <div />
        </div>
      </div>
      )}

      {/* ── DATOS DEL PROPIETARIO ── */}
      <div style={st.card}>
        <div style={st.title}>Datos del Propietario</div>
        <div style={st.grid3}>
          <Txt label="Apellido Paterno" value={form.prop_ap} onChange={v => set('prop_ap', v)} />
          <Txt label="Apellido Materno" value={form.prop_am} onChange={v => set('prop_am', v)} />
          <Txt label="Nombre(s) / Razón Social" value={form.prop_nombre_razon_social} onChange={v => set('prop_nombre_razon_social', v)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '17fr 83fr'), gap: 14, marginBottom: 4 }}>
          <Txt label="RFC del Propietario" value={form.prop_rfc} onChange={v => set('prop_rfc', v.toUpperCase())} maxLength={13} />
          <div />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '43fr 17fr 40fr'), gap: 14, marginBottom: 4 }}>
          <Txt label="Nombre del Acreedor Hipotecario / Fideicomiso" value={form.prop_acreedor_hipotecario} onChange={v => set('prop_acreedor_hipotecario', v)} />
          <Txt label="RFC del Acreedor" value={form.prop_rfc_acreedor} onChange={v => set('prop_rfc_acreedor', v.toUpperCase())} maxLength={13} />
          <div />
        </div>
        <div style={st.sectionTitle}>Domicilio del Propietario</div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '1.4fr 0.5fr 1.1fr'), gap: 14, marginBottom: 4 }}>
          <Txt label="Calle y Número" value={form.prop_calle_numero} onChange={v => set('prop_calle_numero', v)} />
          <div style={st.field}>
            <label style={st.label}>C.P.</label>
            <input value={form.prop_cp} maxLength={5} onChange={e => setCp('prop', 'prop_cp', e.target.value)} style={st.input} placeholder="5 dígitos" />
          </div>
          <div />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '2fr 1.5fr 2fr 0.5fr 0.6fr 0.6fr 2fr'), gap: 14, marginBottom: 4 }}>
          <div style={st.field}>
            <label style={st.label}>Colonia</label>
            <select value={form.prop_colonia} onChange={e => set('prop_colonia', e.target.value)} style={st.select}>
              <option value="">— Selecciona —</option>
              {coloniasProp.map((c, i) => <option key={i} value={c.colonia}>{c.colonia}</option>)}
            </select>
          </div>
          <div style={st.field}>
            <label style={st.label}>Municipio o Delegación</label>
            <input value={form.prop_municipio} readOnly style={st.inputReadonly} />
          </div>
          <div style={st.field}>
            <label style={st.label}>Entidad Federativa</label>
            <input value={form.prop_estado} readOnly style={st.inputReadonly} />
          </div>
          <Txt label="Lada" value={form.prop_lada} onChange={v => set('prop_lada', v)} maxLength={6} />
          <Txt label="Teléfono" value={form.prop_telefono} onChange={v => set('prop_telefono', v)} maxLength={10} />
          <Txt label="Ext." value={form.prop_ext} onChange={v => set('prop_ext', v)} maxLength={10} />
          <div />
        </div>
      </div>

      {/* ── DATOS DE LA VIVIENDA ── */}
      <div style={st.card}>
        <div style={st.title}>Datos de la Vivienda Objeto de Crédito</div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '3fr 0.8fr 0.8fr 0.8fr 0.8fr'), gap: 14, marginBottom: 4 }}>
          <Txt label="Calle" value={form.viv_calle_numero} onChange={v => set('viv_calle_numero', v)} />
          <Txt label="No. Ext" value={form.viv_numero_ext} onChange={v => set('viv_numero_ext', v)} />
          <Txt label="No. Int" value={form.viv_numero_int} onChange={v => set('viv_numero_int', v)} />
          <Txt label="Manzana" value={form.viv_manzana} onChange={v => set('viv_manzana', v)} />
          <Txt label="Lote" value={form.viv_lote} onChange={v => set('viv_lote', v)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '7fr 24fr 29fr 15fr 25fr'), gap: 14, marginBottom: 4 }}>
          <div style={st.field}>
            <label style={st.label}>C.P.</label>
            <input value={form.viv_cp} maxLength={5} onChange={e => setCp('viv', 'viv_cp', e.target.value)} style={st.input} placeholder="5 dígitos" />
          </div>
          <Txt label="Colonia" value={form.viv_colonia} onChange={v => set('viv_colonia', v)} />
          <div style={st.field}>
            <label style={st.label}>Municipio o Delegación</label>
            <input value={form.viv_municipio} readOnly style={st.inputReadonly} />
          </div>
          <div style={st.field}>
            <label style={st.label}>Entidad Federativa</label>
            <input value={form.viv_estado} readOnly style={st.inputReadonly} />
          </div>
          <div />
        </div>
        <div style={{ width: isMobile ? '50%' : '20%' }}>
          <div style={st.field}>
            <label style={st.label}>Antigüedad (años)</label>
            <input type="number" min="0" max="99" value={form.viv_antiguedad_anos || ''} onChange={e => set('viv_antiguedad_anos', e.target.value)} style={st.input} />
          </div>
        </div>
      </div>

      {/* ── DISCAPACIDAD ── */}
      <div style={st.card}>
        <div style={st.title}>Discapacidad</div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '1fr 3fr'), gap: 14, marginBottom: 4 }}>
          <Sel label="¿Vivienda para persona discapacitada?" value={form.discapacitado} onChange={v => { set('discapacitado', v); if (v !== 'Si') { set('tipo_incapacidad', ''); set('discapacidad_quien', ''); } }} options={['Si', 'No']} />
          <div />
        </div>
        {form.discapacitado === 'Si' && (
          <div style={st.grid2}>
            <Sel label="Tipo de Incapacidad" value={form.tipo_incapacidad} onChange={v => set('tipo_incapacidad', v)} options={INCAPACID} />
            <Sel label="Persona que presentará comprobante de discapacidad" value={form.discapacidad_quien} onChange={v => set('discapacidad_quien', v)} options={DISC_QUIEN} />
          </div>
        )}
      </div>

      {/* ── REFERENCIAS FAMILIARES ── */}
      <div style={st.card}>
        <div style={st.title}>Referencias Familiares</div>
        <div style={st.grid2}>
          <div>
            <div style={st.sectionTitle}>Referencia 1</div>
            <Txt label="Apellido Paterno" value={form.ref1_ap} onChange={v => set('ref1_ap', v)} />
            <Txt label="Apellido Materno" value={form.ref1_am} onChange={v => set('ref1_am', v)} />
            <Txt label="Nombre(s)" value={form.ref1_nombres} onChange={v => set('ref1_nombres', v)} />
            <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '12fr 19fr 25fr 44fr'), gap: 10 }}>
              <Txt label="Lada" value={form.ref1_lada} onChange={v => set('ref1_lada', v)} maxLength={6} />
              <Txt label="Teléfono" value={form.ref1_telefono} onChange={v => set('ref1_telefono', v)} maxLength={10} />
              <Txt label="Celular" value={form.ref1_celular} onChange={v => set('ref1_celular', v)} maxLength={10} />
              <div />
            </div>
          </div>
          <div>
            <div style={st.sectionTitle}>Referencia 2</div>
            <Txt label="Apellido Paterno" value={form.ref2_ap} onChange={v => set('ref2_ap', v)} />
            <Txt label="Apellido Materno" value={form.ref2_am} onChange={v => set('ref2_am', v)} />
            <Txt label="Nombre(s)" value={form.ref2_nombres} onChange={v => set('ref2_nombres', v)} />
            <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '12fr 19fr 25fr 44fr'), gap: 10 }}>
              <Txt label="Lada" value={form.ref2_lada} onChange={v => set('ref2_lada', v)} maxLength={6} />
              <Txt label="Teléfono" value={form.ref2_telefono} onChange={v => set('ref2_telefono', v)} maxLength={10} />
              <Txt label="Celular" value={form.ref2_celular} onChange={v => set('ref2_celular', v)} maxLength={10} />
              <div />
            </div>
          </div>
        </div>
      </div>

      {/* ── HOJA 3 — ABONO EN CUENTA Y ACREEDOR HIPOTECARIO ── */}
      <div style={st.card}>
        <div style={st.title}>Datos para Abono en Cuenta del Crédito</div>
        <div style={st.grid2}>
          <div>
            <Txt label="Nombre / Denominación / Razón Social (Línea 1)" value={form.abono_nombre_l1} onChange={v => set('abono_nombre_l1', v)} />
            <Txt label="Nombre / Denominación / Razón Social (Línea 2)" value={form.abono_nombre_l2} onChange={v => set('abono_nombre_l2', v)} />
            <Txt label="RFC" value={form.abono_rfc} onChange={v => set('abono_rfc', v.toUpperCase())} maxLength={20} />
            <Txt label="Nombre como en Estado de Cuenta (Línea 1)" value={form.abono_nombre_ec_l1} onChange={v => set('abono_nombre_ec_l1', v)} />
            <Txt label="Nombre como en Estado de Cuenta (Línea 2)" value={form.abono_nombre_ec_l2} onChange={v => set('abono_nombre_ec_l2', v)} />
            <Txt label="CLABE" value={form.abono_clabe} onChange={v => set('abono_clabe', v)} maxLength={18} />
          </div>
          <div>
            <div style={st.sectionTitle}>Acreedor Hipotecario</div>
            <Txt label="Nombre / Denominación / Razón Social (Línea 1)" value={form.acreedor_nombre_l1} onChange={v => set('acreedor_nombre_l1', v)} />
            <Txt label="Nombre / Denominación / Razón Social (Línea 2)" value={form.acreedor_nombre_l2} onChange={v => set('acreedor_nombre_l2', v)} />
            <Txt label="RFC" value={form.acreedor_rfc} onChange={v => set('acreedor_rfc', v.toUpperCase())} maxLength={20} />
            <Txt label="Nombre como en Estado de Cuenta (Línea 1)" value={form.acreedor_nombre_ec_l1} onChange={v => set('acreedor_nombre_ec_l1', v)} />
            <Txt label="Nombre como en Estado de Cuenta (Línea 2)" value={form.acreedor_nombre_ec_l2} onChange={v => set('acreedor_nombre_ec_l2', v)} />
            <Txt label="CLABE" value={form.acreedor_clabe} onChange={v => set('acreedor_clabe', v)} maxLength={18} />
          </div>
        </div>
        <div style={st.sectionTitle}>Número de Crédito Infonavit (si vivienda tiene crédito vigente)</div>
        <div style={st.grid2}>
          <Txt label="Crédito de Titular" value={form.num_credito_titular} onChange={v => set('num_credito_titular', v)} maxLength={20} />
          <Txt label="Crédito de Cónyuge / Familiar / Corresidencial" value={form.num_credito_coparticipante} onChange={v => set('num_credito_coparticipante', v)} maxLength={20} />
        </div>
      </div>

      {/* ── SOLICITUD AVALÚO — DESTINO Y BROKER ── */}
      <div style={st.card}>
        <div style={st.title}>Solicitud Avalúo — Destino y Broker</div>
        <div style={{ display: 'grid', gridTemplateColumns: gridCols(isMobile, isTablet, '0.5fr 0.5fr 1fr'), gap: 14 }}>
          <Sel label="Destino (Solicitud Avalúo)" value={form.destino} onChange={v => set('destino', v)} options={DESTINOS_AVALUO} />
          <div style={st.field}>
            <label style={st.label}>Broker</label>
            {usuarioActual?.rol === 'administrador' ? (
              <select value={form.broker_id || ''} onChange={e => set('broker_id', e.target.value ? Number(e.target.value) : null)} style={st.select}>
                <option value="">— Selecciona —</option>
                {brokers.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
              </select>
            ) : (
              <input value={(soloLectura ? form.broker_nombre : usuarioActual?.broker_nombre) || ''} disabled style={st.select} />
            )}
          </div>
          <div />
        </div>
      </div>

      </fieldset>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button onClick={() => setView('lista')} style={{ ...st.btn, ...st.btnCancel }}>{soloLectura ? 'Cerrar' : 'Cancelar'}</button>
        {!soloLectura && (
          <button onClick={guardar} disabled={guardando} style={{ ...st.btn, ...st.btnPrimary }}>
            {guardando ? 'Guardando...' : (editId ? 'Actualizar' : 'Guardar')}
          </button>
        )}
      </div>
    </div>
  );
}
